/**
 * Cart upsell add control.
 *
 * Two jobs, both deliberately small. Quick add on a product with sizes swaps itself for
 * the sizes, crossfading rather than expanding so the bag does not jump. Quick add on a
 * product without sizes, or any size, adds that variant and lets the bag re-render.
 *
 * Delegated on the document because the drawer replaces its own contents after every
 * add, taking these buttons with it.
 */
(() => {
  const FADE = 160;
  const busy = new WeakSet();

  const reveal = (trigger) => {
    const wrapper = trigger.closest('[data-upsell-add]');
    const sizes = wrapper?.querySelector('[data-upsell-sizes]');
    if (!sizes) return;

    trigger.classList.add('cart-upsell__action--out');
    setTimeout(() => {
      trigger.hidden = true;
      sizes.hidden = false;
      // A frame between unhiding and animating, or the transition has nothing to run from.
      requestAnimationFrame(() => sizes.classList.add('cart-upsell__sizes--in'));
    }, FADE);
  };

  const add = async (button) => {
    busy.add(button);
    button.classList.add('cart-upsell__action--busy');
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(button.dataset.upsellVariant), quantity: 1 }] }),
      });
      if (!response.ok) throw new Error(`Add failed (${response.status})`);
      // Re-renders the bag and the counter without opening anything. The upsell is
      // rendered server-side, so this is also what drops the product from the list.
      if (window.dtRefreshCart) await window.dtRefreshCart();
    } catch (error) {
      console.log(`cart upsell add: ${error.message}`);
    } finally {
      button.classList.remove('cart-upsell__action--busy');
      busy.delete(button);
    }
  };

  // --- paging -----------------------------------------------------------------

  // The list's own state is a different attribute from the items' page index. Reusing
  // data-upsell-page for both made the <ul> match any query for the items.
  const pageOf = (list) => Number(list.dataset.upsellCurrent || 0);

  // On a phone the drawer is the whole screen, so three upsell rows push the cart itself
  // out of view. Show one at a time there; the arrows still reach the rest. The server
  // renders pages of cart_upsell_max_products, so the page indices are reassigned here
  // rather than being fixed at render time.
  const PHONE = '(max-width: 749px)';
  const perPageFor = (list) => (window.matchMedia(PHONE).matches ? 1 : Number(list.dataset.upsellPerPage) || 3);

  const paginate = (list) => {
    const items = [...list.querySelectorAll('[data-upsell-page]')];
    if (!items.length) return;
    const per = perPageFor(list);
    items.forEach((item, i) => {
      item.dataset.upsellPage = String(Math.floor(i / per));
    });
    const nav = list.closest('.cart-upsell')?.querySelector('[data-upsell-nav]');
    if (nav) nav.hidden = items.length <= per;
    showPage(list, Math.min(pageOf(list), Math.ceil(items.length / per) - 1));
  };

  const paginateAll = () => document.querySelectorAll('[data-upsell-list]').forEach(paginate);

  const showPage = (list, index) => {
    const items = [...list.querySelectorAll('[data-upsell-page]')];
    const pages = Math.max(...items.map((i) => Number(i.dataset.upsellPage))) + 1;
    const next = Math.min(Math.max(index, 0), pages - 1);
    list.dataset.upsellCurrent = String(next);
    items.forEach((item) => {
      item.hidden = Number(item.dataset.upsellPage) !== next;
    });
    const nav = list.closest('.cart-upsell')?.querySelector('[data-upsell-nav]');
    nav?.querySelector('[data-upsell-prev]')?.toggleAttribute('disabled', next === 0);
    nav?.querySelector('[data-upsell-next]')?.toggleAttribute('disabled', next === pages - 1);
  };

  // The drawer re-renders itself after every cart change, so repaginate on those too.
  const schedule = () => requestAnimationFrame(paginateAll);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', schedule);
  else schedule();
  window.matchMedia(PHONE).addEventListener('change', schedule);
  document.addEventListener('cart:refreshed', schedule);

  document.addEventListener('click', (event) => {
    const arrow = event.target.closest('[data-upsell-prev], [data-upsell-next]');
    if (arrow && !arrow.disabled) {
      event.preventDefault();
      const list = arrow.closest('.cart-upsell')?.querySelector('[data-upsell-list]');
      if (list) showPage(list, pageOf(list) + (arrow.hasAttribute('data-upsell-next') ? 1 : -1));
      return;
    }

    const opener = event.target.closest('[data-upsell-open]');
    if (opener) {
      event.preventDefault();
      reveal(opener);
      return;
    }

    const button = event.target.closest('[data-upsell-variant]');
    if (!button || button.disabled || busy.has(button)) return;
    event.preventDefault();
    add(button);
  });
})();
